import { useNavigate } from 'react-router';

import { NonAuthenticatedRoute } from '@/app/constant';
import { Input } from '@/components/form/Input';
import { Inputs } from '@/components/form/Inputs';
import {
  isPasswordNotValid,
  PasswordRequirement,
} from '@/components/legacy-ui/PasswordRequirement';
import { Button } from '@/components/ui/button';
import { Disclaimer } from '@/sections/signup/Disclaimer';
import { SignupError } from '@/sections/signup/SignupError';
import { SSO } from '@/sections/signup/SSO';
import { useAuth } from '@/state/auth';
import { generatePathWithSearch } from '@/utils/url.util';

const UserLambdaValidationException =
  'PreSignUp failed with error email validation failed.';
const UserLambdaNotAuthorized =
  'PreSignUp failed with error email not authorized for registration.';

export const EmailPasswordForm = ({
  onNext,
  isLogin = false,
}: {
  onNext?: () => void;
  isLogin?: boolean;
}) => {
  const {
    isLoading,
    login,
    signup,
    error,
    setError,
    credentials,
    setCredentials,
  } = useAuth();
  const { username, password } = credentials;
  const navigate = useNavigate();

  return (
    <form
      className="flex flex-col gap-4 p-2 md:gap-6"
      id="signup"
      onSubmit={e => {
        e.preventDefault();
        if (isLogin) {
          login(username, password);
        } else {
          signup(username, password, onNext ?? (() => {}));
        }
      }}
    >
      <Inputs
        inputs={[
          {
            label: 'Business Email Address',
            value: credentials.username,
            placeholder: '',
            name: 'username',
            required: true,
          },
          {
            label: 'Password',
            value: credentials.password || '',
            placeholder: '',
            name: 'password',
            type: Input.Type.PASSWORD,
            required: true,
          },
        ]}
        onChange={values => {
          setError('');
          setCredentials(auth => ({ ...auth, ...values }));
        }}
      />
      {/* Regex satisfactions */}
      {!isLogin && <PasswordRequirement password={password} />}
      {/* Disable continue on Signup till all regex are satisfied */}
      <Button
        disabled={
          isLogin ? isLoading : isLoading || isPasswordNotValid({ password })
        }
        className="w-full text-white"
        type="submit"
        id="signup"
      >
        Continue
      </Button>
      <SignupError
        error={
          error && error === UserLambdaValidationException
            ? "Sign up failed. Please ensure you're using a business email address."
            : error && error === UserLambdaNotAuthorized
              ? "You have not been invited to an account yet. Please reach out to your organization's Chariot point of contact to invite you. You can also reach out to support@praetorian.com for more information."
              : error
        }
      />
      {isLogin && (
        <div className="flex justify-between">
          <Button
            onClick={() => {
              setCredentials({ username: '', password: '' });
              navigate(
                generatePathWithSearch({
                  pathname: NonAuthenticatedRoute.SIGNUP,
                })
              );
            }}
            className="text-md p-0"
            variant="link"
          >
            Sign Up
          </Button>
          <Button
            onClick={() =>
              navigate(
                generatePathWithSearch({
                  pathname: NonAuthenticatedRoute.FORGOT_PASSWORD,
                })
              )
            }
            className="text-md p-0"
            variant="link"
          >
            Forgot Password?
          </Button>
        </div>
      )}
      <SSO />
      <Disclaimer />
    </form>
  );
};
